import { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Site, BulkCreateSiteRequest, Team } from '@cmms/types'
import { motion } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { primary } from '../../theme/palette'
import { MapPicker } from '../../components/MapPicker'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { QrCodeDialog } from '../../components/QrCodeDialog'
import { UserAvatar } from '../../components/UserAvatar'
import { useAuthStore } from '../../store/authStore'
import { QRCodeCanvas } from 'qrcode.react'
import { useCustomerStore } from '../../store/customerStore'
import { formatSiteName } from '../../lib/siteDisplayName'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { usePermissions } from '../../hooks/usePermissions'

const SITE_COLORS = ['#0D9488', '#D97706', '#0E7490', '#7C3AED', '#EA580C', '#059669', '#2563EB', '#DB2777']

interface Props {
  siteId: string | null
  onSelectSite: (id: string | null) => void
}

export function SitesTab({ siteId, onSelectSite }: Props) {
  const qc = useQueryClient()
  const { can } = usePermissions()
  const [searchParams, setSearchParams] = useSearchParams()

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list() })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; site: Site } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Site | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  // Free-text filter for the sidebar. Case-insensitive substring match
  // across name + address — the two fields users actually scan for.
  const [search, setSearch] = useState('')
  // Multi-select state. A non-empty set means the list is in
  // "select mode" — rows show checkboxes and clicking a row toggles
  // selection instead of opening detail. Bulk-delete acts on this set.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)

  const filteredSites = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return sites
    return sites.filter((s) =>
      s.name.toLowerCase().includes(q)
      || (s.address ?? '').toLowerCase().includes(q),
    )
  }, [sites, search])

  const selected = siteId ? sites.find((s) => s.id === siteId) ?? null : null
  // Auto-select the first site when none is in the URL — keeps the
  // detail panel populated rather than showing an empty placeholder.
  // Skip while the user is multi-selecting; opening detail mid-bulk
  // would be jarring.
  if (!siteId && sites.length > 0 && selectedIds.size === 0) onSelectSite(sites[0]!.id)

  // Deep-link from "Edit in Settings" on the Locations page lands at
  // /settings/sites/<id>?edit=1 — honor the flag by opening the edit
  // dialog as soon as the matching site is loaded, then drop the
  // param so a refresh doesn't keep reopening it.
  useEffect(() => {
    if (searchParams.get('edit') !== '1' || !selected || dialog) return
    setDialog({ mode: 'edit', site: selected })
    const next = new URLSearchParams(searchParams)
    next.delete('edit')
    setSearchParams(next, { replace: true })
  }, [selected, searchParams, dialog, setSearchParams])

  const createM = useMutation({
    mutationFn: (body: { name: string; description?: string; address?: string; latitude?: number; longitude?: number; customer_id?: string }) =>
      api.sites.create(body),
    onSuccess: (s) => {
      toast.success('Site created')
      qc.invalidateQueries({ queryKey: ['sites'] })
      qc.invalidateQueries({ queryKey: ['customers'] })
      setDialog(null)
      if (s?.id) onSelectSite(s.id)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const updateM = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; description?: string; address?: string; latitude?: number; longitude?: number }) =>
      api.sites.update(id, body),
    onSuccess: () => { toast.success('Site updated'); qc.invalidateQueries({ queryKey: ['sites'] }); setDialog(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const deleteM = useMutation({
    mutationFn: (id: string) => api.sites.remove(id),
    onSuccess: () => {
      toast.success('Site deleted')
      qc.invalidateQueries({ queryKey: ['sites'] })
      setConfirmDelete(null)
      onSelectSite(null)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Has locations/assets — remove them first.'),
  })

  // Bulk delete fans out one DELETE per selected site in parallel and
  // reports the aggregate result. We use `allSettled` so a single site
  // with locations / assets doesn't abort the rest of the batch — the
  // user can clear the surviving rows and retry.
  const bulkDeleteM = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.sites.remove(id)))
      const failed = results.filter((r) => r.status === 'rejected').length
      return { total: ids.length, failed }
    },
    onSuccess: ({ total, failed }) => {
      qc.invalidateQueries({ queryKey: ['sites'] })
      setConfirmBulkDelete(false)
      setSelectedIds(new Set())
      // If the currently-open site was wiped, drop the right-panel
      // selection too so we don't render stale detail.
      if (siteId && selectedIds.has(siteId)) onSelectSite(null)
      if (failed === 0) {
        toast.success(`Deleted ${total} site${total === 1 ? '' : 's'}`)
      } else if (failed === total) {
        toast.error("Couldn't delete — sites have locations or assets attached")
      } else {
        toast.error(`Deleted ${total - failed} · ${failed} skipped (has locations or assets)`)
      }
    },
    onError: () => toast.error('Bulk delete failed'),
  })

  const toggleSelect = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const selectMode = selectedIds.size > 0
  // "Select all" applies to the currently-filtered list, so the user
  // can search "warehouse", check the header box, and bulk-delete just
  // those rows.
  const allFilteredSelected = filteredSites.length > 0
    && filteredSites.every((s) => selectedIds.has(s.id))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {selectMode
            ? `${selectedIds.size} selected`
            : `${sites.length} site${sites.length === 1 ? '' : 's'} in your organization`}
        </Typography>
        <Stack direction="row" spacing={1.25}>
          {selectMode ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setSelectedIds(new Set())}
                sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Tooltip title={can('sites:delete') ? '' : "You don't have permission"} arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setConfirmBulkDelete(true)}
                    disabled={!can('sites:delete')}
                    sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2 }}
                  >
                    Delete {selectedIds.size}
                  </Button>
                </Box>
              </Tooltip>
            </>
          ) : (
            <>
              {/* CSV import — same permission gate as "New site" so the
                  same admin can bring in a whole spreadsheet of sites at
                  once after the initial setup. */}
              <Tooltip title={can('sites:create') ? '' : "You don't have permission"} arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setImportOpen(true)}
                    disabled={!can('sites:create')}
                    sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2 }}
                  >
                    Import CSV
                  </Button>
                </Box>
              </Tooltip>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
                <Tooltip title={can('sites:create') ? '' : "You don't have permission"} arrow>
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setDialog({ mode: 'create' })} disabled={!can('sites:create')}
                      sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, boxShadow: primary.glow }}>New site</Button>
                  </Box>
                </Tooltip>
              </motion.div>
            </>
          )}
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 480, display: 'flex', flexDirection: { xs: 'column', md: 'row' },
        border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: 3 }}>

        {sitesQ.isLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            {/* Left — site list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: { xs: '100%', md: 360 }, flexShrink: 0,
              borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
              {/* Search box — filters the list as the user types. The
                  count next to it updates to reflect what's visible so
                  the user knows they aren't being shown the whole set. */}
              <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <TextField
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search sites…"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                    sx: { fontSize: 13.5, borderRadius: 1.5 },
                  }}
                />
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}
                sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                {/* Select-all checkbox. When `select mode` is active or
                    the user clicks it from a calm list, it picks every
                    currently-visible (i.e. filtered) row. */}
                <Tooltip title={can('sites:delete') ? 'Select all visible' : "You don't have permission"} arrow>
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Checkbox
                      size="small"
                      checked={allFilteredSelected && filteredSites.length > 0}
                      indeterminate={selectedIds.size > 0 && !allFilteredSelected}
                      disabled={!can('sites:delete') || filteredSites.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds((prev) => {
                            const next = new Set(prev)
                            filteredSites.forEach((s) => next.add(s.id))
                            return next
                          })
                        } else {
                          setSelectedIds((prev) => {
                            const next = new Set(prev)
                            filteredSites.forEach((s) => next.delete(s.id))
                            return next
                          })
                        }
                      }}
                      sx={{ p: 0.5 }}
                    />
                  </Box>
                </Tooltip>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
                  {search
                    ? `${filteredSites.length} of ${sites.length}`
                    : `${sites.length} site${sites.length === 1 ? '' : 's'}`}
                </Typography>
              </Stack>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1,
                scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
                '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
                '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
                {filteredSites.map((site, i) => {
                  const sel = site.id === siteId
                  const checked = selectedIds.has(site.id)
                  const color = SITE_COLORS[sites.indexOf(site) % SITE_COLORS.length]!
                  // Row click toggles selection when select-mode is
                  // active; otherwise opens detail. Keeps the gesture
                  // unambiguous — once you've selected one row, the
                  // whole list becomes a checkbox grid.
                  const handleRowClick = () => {
                    if (selectMode) toggleSelect(site.id)
                    else onSelectSite(site.id)
                  }
                  return (
                    <motion.div key={site.id} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(0.4, i * 0.015), duration: 0.24 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} onClick={handleRowClick}
                        sx={{
                          position: 'relative', px: 2.5, py: 1.5, cursor: 'pointer',
                          transition: 'background-color 120ms ease',
                          bgcolor: checked
                            ? (t) => t.palette.mode === 'light' ? 'rgba(220,38,38,0.06)' : 'rgba(248,113,113,0.08)'
                            : sel && !selectMode ? primary.selected : 'transparent',
                          '&:hover': {
                            bgcolor: checked
                              ? (t) => t.palette.mode === 'light' ? 'rgba(220,38,38,0.1)' : 'rgba(248,113,113,0.12)'
                              : sel && !selectMode ? primary.selectedHover
                                : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)',
                          },
                          '&::before': sel && !selectMode ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
                        }}>
                        <Checkbox
                          size="small"
                          checked={checked}
                          disabled={!can('sites:delete')}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSelect(site.id)}
                          sx={{ p: 0.5, ml: -0.5 }}
                        />
                        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: `${color}14`, color, flexShrink: 0 }}>
                          <BusinessOutlinedIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: sel ? 600 : 500 }} noWrap>{formatSiteName(site)}</Typography>
                          {site.address && <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.15 }} noWrap>{site.address}</Typography>}
                        </Box>
                      </Stack>
                    </motion.div>
                  )
                })}
                {filteredSites.length === 0 && (
                  <Box sx={{ py: 6, textAlign: 'center', px: 3 }}>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {sites.length === 0
                        ? 'No sites yet. Create one.'
                        : `No sites match "${search}".`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Right — site detail */}
            {selected ? <SiteDetail site={selected} color={SITE_COLORS[sites.indexOf(selected) % SITE_COLORS.length]!}
              onEdit={() => setDialog({ mode: 'edit', site: selected })}
              onDelete={() => setConfirmDelete(selected)} /> : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select a site to view details.</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {dialog && (
        <SiteFormDialog
          mode={dialog.mode}
          site={dialog.mode === 'edit' ? dialog.site : undefined}
          onClose={() => setDialog(null)}
          onSubmit={(data) => dialog.mode === 'create' ? createM.mutate(data) : updateM.mutate({ id: dialog.site.id, ...data })}
          loading={createM.isPending || updateM.isPending}
        />
      )}

      <ImportSitesDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(n) => {
          toast.success(`Imported ${n} site${n === 1 ? '' : 's'}`)
          qc.invalidateQueries({ queryKey: ['sites'] })
          setImportOpen(false)
        }}
      />

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete {confirmDelete?.name}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            This will permanently delete the site and cascade to all its locations. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => confirmDelete && deleteM.mutate(confirmDelete.id)}
            disabled={deleteM.isPending} sx={{ fontSize: 13, fontWeight: 600 }}>
            {deleteM.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmBulkDelete} onClose={() => !bulkDeleteM.isPending && setConfirmBulkDelete(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Delete {selectedIds.size} site{selectedIds.size === 1 ? '' : 's'}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1.5 }}>
            This will permanently delete the selected sites and cascade to all their
            locations. Sites that still have locations or assets attached will be
            skipped — you can remove those first and retry.
          </Typography>
          <Box sx={{
            maxHeight: 180, overflowY: 'auto', border: '1px solid', borderColor: 'divider',
            borderRadius: 1.5, p: 1.25, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
          }}>
            {sites.filter((s) => selectedIds.has(s.id)).map((s) => (
              <Typography key={s.id} sx={{ fontSize: 12.5, py: 0.25 }}>• {s.name}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmBulkDelete(false)}
            disabled={bulkDeleteM.isPending} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" color="error"
            onClick={() => bulkDeleteM.mutate(Array.from(selectedIds))}
            disabled={bulkDeleteM.isPending} sx={{ fontSize: 13, fontWeight: 600 }}>
            {bulkDeleteM.isPending ? 'Deleting…' : `Delete ${selectedIds.size}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function SiteDetail({ site, color, onEdit, onDelete }: { site: Site; color: string; onEdit: () => void; onDelete: () => void }) {
  const navigate = useNavigate()
  const { formatDate } = useOrgSettings()
  const { can } = usePermissions()
  const hasCoords = site.latitude != null && site.longitude != null

  return (
    <motion.div key={site.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26 }}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 3.5, pb: 2.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2.5}>
          <Box sx={{ width: 52, height: 52, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${color}14`, color, flexShrink: 0 }}>
            <BusinessOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{formatSiteName(site)}</Typography>
            {site.address && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{site.address}</Typography>
              </Stack>
            )}
            {site.description && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, lineHeight: 1.55 }}>{site.description}</Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Tooltip title={can('sites:update') ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={onEdit} disabled={!can('sites:update')}
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider' }}>Edit</Button></Box>
            </Tooltip>
            <Tooltip title={can('sites:delete') ? 'Delete' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><IconButton size="small" onClick={onDelete} disabled={!can('sites:delete')}
                sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, '&:hover': { color: 'error.main' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton></Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>

        {hasCoords && (
          <Box sx={{ mb: 3 }}>
            <MapPicker latitude={site.latitude} longitude={site.longitude} height={220}
              onChange={() => {}} />
          </Box>
        )}

        {/* HSE fork: the CMMS "Navigate" section pointed to Locations,
            Assets, and Work Orders — all stripped in HSE. Replaced with
            the public site-QR card so admins can grab the sticker
            that routes members of the public to the risk-report form. */}
        <Sec title="Public risk-report QR">
          <SiteQrCard site={site} />
        </Sec>

        <Divider />

        {/* HSE fork: linked-team card with roster + reassign
            affordance. Sits between the QR and the plain details so
            supervisors can see who's responsible for this site
            without opening the edit dialog. */}
        <Sec title="Responsible team">
          <SiteTeamCard site={site} />
        </Sec>

        <Divider />

        <Sec title="Details">
          <Stack spacing={1.25}>
            <Kv label="Name" value={site.name} />
            {site.description && <Kv label="Description" value={site.description} />}
            {site.address && <Kv label="Address" value={site.address} />}
            {site.entity && <Kv label="Entity" value={site.entity} />}
            {site.state && <Kv label="State" value={site.state} />}
            {site.branch_area_sqft != null && (
              <Kv label="Branch area" value={`${site.branch_area_sqft.toLocaleString()} sq ft`} />
            )}
            {site.category && <Kv label="Category" value={site.category} />}
            {site.field1 && <Kv label="Custom field 1" value={site.field1} />}
            {site.field2 && <Kv label="Custom field 2" value={site.field2} />}
            {site.field3 && <Kv label="Custom field 3" value={site.field3} />}
            {site.field4 && <Kv label="Custom field 4" value={site.field4} />}
            {site.field5 && <Kv label="Custom field 5" value={site.field5} />}
            {hasCoords && <Kv label="Coordinates" value={`${site.latitude!.toFixed(6)}, ${site.longitude!.toFixed(6)}`} />}
            <Kv label="Created" value={formatDate(site.created_at)} />
          </Stack>
        </Sec>
      </Box>
    </motion.div>
  )
}

// HSE fork: `NavCard` was the CMMS "Navigate → Locations / Assets /
// Work Orders" shortcut. All three modules were stripped, so the
// helper has been removed alongside its consumers.

function SiteFormDialog({ mode, site, onClose, onSubmit, loading }: {
  mode: 'create' | 'edit'; site?: Site; onClose: () => void
  onSubmit: (data: {
    name: string; description?: string; address?: string;
    latitude?: number; longitude?: number; customer_id?: string;
    entity?: string | null; state?: string | null;
    branch_area_sqft?: number | null;
    team_id?: string | null;
    category?: string | null;
    field1?: string | null; field2?: string | null;
    field3?: string | null; field4?: string | null;
    field5?: string | null;
  }) => void
  loading: boolean
}) {
  // Pre-fill customer_id from the active switcher context on create so
  // the common case ("I'm working on Customer X, create a site in X")
  // doesn't need an extra click. On edit, use the site's existing
  // customer_id.
  const activeCustomerId = useCustomerStore((s) => s.customerId)
  const [name, setName] = useState(site?.name ?? '')
  const [desc, setDesc] = useState(site?.description ?? '')
  const [address, setAddress] = useState(site?.address ?? '')
  const [lat, setLat] = useState<number | null>(site?.latitude ?? null)
  const [lng, setLng] = useState<number | null>(site?.longitude ?? null)
  const [customerId, setCustomerId] = useState<string | null>(
    site?.customer_id ?? activeCustomerId ?? null,
  )
  // Branch meta-data — entity (operating company), state (jurisdiction),
  // branch_area_sqft (built-up area). All optional.
  const [entity, setEntity] = useState(site?.entity ?? '')
  const [stateField, setStateField] = useState(site?.state ?? '')
  const [areaSqft, setAreaSqft] = useState<string>(
    site?.branch_area_sqft != null ? String(site.branch_area_sqft) : '',
  )
  // HSE fork: linked team. Auto-assigns the request when a customer
  // scans this site's QR sticker and submits a risk report. Optional
  // — sites without a linked team still accept reports; those land
  // in Requests unassigned for triage.
  const [teamId, setTeamId] = useState<string | null>(
    (site as (Site & { team_id?: string | null }))?.team_id ?? null,
  )
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.teams.list(),
  })
  const teams: Team[] = Array.isArray(teamsQuery.data) ? teamsQuery.data : []
  // Customer-configurable site metadata. Category is a free-text bucket
  // (Branch / Warehouse / Datacentre / …); field1..5 are generic
  // placeholder slots the customer will rename meaningfully later. All
  // optional, all free text.
  const [category, setCategory] = useState(site?.category ?? '')
  const [field1, setField1] = useState(site?.field1 ?? '')
  const [field2, setField2] = useState(site?.field2 ?? '')
  const [field3, setField3] = useState(site?.field3 ?? '')
  const [field4, setField4] = useState(site?.field4 ?? '')
  const [field5, setField5] = useState(site?.field5 ?? '')

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {mode === 'create' ? 'Create a new site' : `Edit ${site?.name}`}
      </DialogTitle>
      <DialogContent>
        {mode === 'create' && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
            Sites are the top level — a building complex, campus, or facility. Pin the location on the map.
          </Typography>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <TextField label="Site name" required value={name} onChange={(e) => setName(e.target.value)}
              size="small" fullWidth autoFocus placeholder="e.g. North Warehouse"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            <CustomerScopePicker
              mode="required"
              value={customerId}
              onChange={setCustomerId}
              disabled={mode === 'edit'}
              helperText={mode === 'edit'
                ? "A site's customer can't change after creation — clone the site if you need to move it."
                : 'Sites belong to exactly one customer. Locations + assets + work orders inherit this.'}
            />

            <TextField label="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)}
              size="small" fullWidth multiline minRows={2} placeholder="What is this site used for?"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {/* Address is editable — picking on the map prefills it,
                but admins can override or type one in directly when
                the map's reverse-geocode is wrong. CSV imports also
                set this field. */}
            <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)}
              size="small" fullWidth multiline minRows={2}
              placeholder="Street, area, city — or pick on the map"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {/* Branch meta-data row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Entity (optional)" value={entity} onChange={(e) => setEntity(e.target.value)}
                size="small" fullWidth placeholder="e.g. Bacsys Operations Pvt Ltd"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
              <TextField label="State (optional)" value={stateField} onChange={(e) => setStateField(e.target.value)}
                size="small" fullWidth placeholder="e.g. Tamil Nadu"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            </Stack>
            <TextField label="Branch area (sq ft, optional)" value={areaSqft}
              onChange={(e) => {
                // Strip anything that isn't a digit so the form can't
                // submit a junk integer the backend would reject.
                setAreaSqft(e.target.value.replace(/[^0-9]/g, ''))
              }}
              size="small" fullWidth placeholder="e.g. 4500"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {/* HSE fork: linked team dropdown. Blank = no team assigned;
                risk reports scanned from this site's QR sticker land in
                Requests unassigned for triage. Pick a team and reports
                auto-assign to it. */}
            <TextField
              select
              label="Responsible team (optional)"
              value={teamId ?? ''}
              onChange={(e) => setTeamId(e.target.value === '' ? null : e.target.value)}
              size="small" fullWidth
              helperText="Public risk reports scanned from this site's QR sticker will be auto-assigned to this team."
              slotProps={{
                select: { native: true },
                inputLabel: { shrink: true },
              }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }}
            >
              <option value="">— No team —</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </TextField>

            {/* ── Category + 5 customer-configurable custom fields ──
                The customer asked for a category bucket + five generic
                slots they can rename later (asset tag, owner, contract
                id, …). Stored as plain text — labels live in the UI,
                column names stay stable so we don't have to migrate
                whenever they change their minds. */}
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: 'text.secondary' }}>
              Category & custom fields
            </Typography>
            <TextField label="Category (optional)" value={category}
              onChange={(e) => setCategory(e.target.value)}
              size="small" fullWidth placeholder="e.g. Branch, Warehouse, Datacentre"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Custom field 1" value={field1}
                onChange={(e) => setField1(e.target.value)}
                size="small" fullWidth placeholder="Optional"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
              <TextField label="Custom field 2" value={field2}
                onChange={(e) => setField2(e.target.value)}
                size="small" fullWidth placeholder="Optional"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Custom field 3" value={field3}
                onChange={(e) => setField3(e.target.value)}
                size="small" fullWidth placeholder="Optional"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
              <TextField label="Custom field 4" value={field4}
                onChange={(e) => setField4(e.target.value)}
                size="small" fullWidth placeholder="Optional"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            </Stack>
            <TextField label="Custom field 5" value={field5}
              onChange={(e) => setField5(e.target.value)}
              size="small" fullWidth placeholder="Optional"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {lat != null && lng != null && (
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.25 }}>Latitude</Typography>
                  <Typography sx={{ fontSize: 13, fontFamily: 'ui-monospace, monospace' }}>{lat.toFixed(6)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.25 }}>Longitude</Typography>
                  <Typography sx={{ fontSize: 13, fontFamily: 'ui-monospace, monospace' }}>{lng.toFixed(6)}</Typography>
                </Box>
              </Stack>
            )}
          </Stack>

          <Box sx={{ flex: 1.3, minWidth: 0, minHeight: 360 }}>
            <MapPicker
              latitude={lat}
              longitude={lng}
              height={380}
              onChange={(newLat, newLng, newAddr) => {
                setLat(newLat)
                setLng(newLng)
                if (newAddr) setAddress(newAddr)
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || !customerId || loading}
          onClick={() => {
            // Empty-string → null on edit so admins can clear a field;
            // undefined on create so the backend leaves the column null.
            const parsedArea = areaSqft.trim() === '' ? null : parseInt(areaSqft, 10)
            onSubmit({
              name: name.trim(),
              description: desc.trim() || undefined,
              address: address.trim() || undefined,
              latitude: lat ?? undefined,
              longitude: lng ?? undefined,
              entity: entity.trim() || (mode === 'edit' ? null : undefined),
              state: stateField.trim() || (mode === 'edit' ? null : undefined),
              branch_area_sqft: Number.isFinite(parsedArea as number)
                ? (parsedArea as number)
                : (mode === 'edit' ? null : undefined),
              // HSE fork: linked team — null clears the assignment
              // on edit; undefined leaves the column untouched.
              team_id: teamId ?? (mode === 'edit' ? null : undefined),
              category: category.trim() || (mode === 'edit' ? null : undefined),
              field1: field1.trim() || (mode === 'edit' ? null : undefined),
              field2: field2.trim() || (mode === 'edit' ? null : undefined),
              field3: field3.trim() || (mode === 'edit' ? null : undefined),
              field4: field4.trim() || (mode === 'edit' ? null : undefined),
              field5: field5.trim() || (mode === 'edit' ? null : undefined),
              // Only send on create — edits can't reassign customers.
              ...(mode === 'create' && customerId ? { customer_id: customerId } : {}),
            })
          }}
          sx={{ fontSize: 13, fontWeight: 600 }}>
          {loading ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create site' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return <Box sx={{ py: 2.5 }}><Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.25 }}>{title}</Typography>{children}</Box>
}
function Kv({ label, value }: { label: string; value: string }) {
  return <Stack direction="row" alignItems="flex-start" spacing={1.5}><Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 90 }}>{label}</Typography><Typography sx={{ fontSize: 13, fontWeight: 500 }}>{value}</Typography></Stack>
}

// ─── CSV bulk-import ────────────────────────────────────────────────────────
//
// Self-contained CSV parser + preview dialog. We don't pull in a CSV
// library (papaparse etc.) because the supported shape is tiny — five
// columns, no nested escaping, no Excel quirks worth worrying about
// — and the inline 40-line parser handles all of it. Quoted values
// support embedded commas and doubled-quote escapes; everything else
// is a plain comma-delimited line. UTF-8 BOM is stripped.

interface ParsedRow {
  /** 1-based, matches the user's spreadsheet row (after the header). */
  rowNum: number
  name: string
  description: string
  address: string
  latitude: number | null
  longitude: number | null
  entity: string
  state: string
  branch_area_sqft: number | null
  category: string
  field1: string
  field2: string
  field3: string
  field4: string
  field5: string
  /** HSE fork: responsible-team name. Blank = no team linked at
   *  import time. Backend resolves name → id and rejects the row
   *  with a helpful error if the name doesn't match any team. */
  team: string
  /** Parse-time complaint about this row (e.g. "Missing name"). The
   *  Import button stays disabled while any row has a non-null error. */
  error: string | null
}

// HSE fork: `default_building` + `default_building_checklist` columns
// were removed. Locations and Checklists are both stripped from HSE,
// so there's nothing for the flag to create or attach.
const CSV_HEADERS = [
  'name', 'description', 'address', 'latitude', 'longitude',
  'entity', 'state', 'branch_area_sqft',
  'category', 'field1', 'field2', 'field3', 'field4', 'field5',
  'team',
] as const
type CsvField = (typeof CSV_HEADERS)[number]

/// Parse one CSV line into cells. Handles "quoted, values" and "" as
/// an escaped quote inside a quoted value. Anything else is split on
/// commas literally.
function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else { inQuotes = false }
      } else {
        cur += ch
      }
    } else {
      if (ch === '"' && cur.length === 0) inQuotes = true
      else if (ch === ',') { out.push(cur); cur = '' }
      else cur += ch
    }
  }
  out.push(cur)
  return out
}

function parseSitesCsv(text: string): { rows: ParsedRow[]; headerError: string | null } {
  // Strip UTF-8 BOM and normalise line endings so Excel-on-Windows
  // (CRLF) and Numbers/Mac (CR) exports both parse cleanly.
  const cleaned = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n')
  const lines = cleaned.split('\n').filter((l) => l.trim().length > 0)
  if (lines.length === 0) {
    return { rows: [], headerError: 'File is empty' }
  }

  const header = parseCsvLine(lines[0]!).map((h) => h.trim().toLowerCase())
  const colIndex: Partial<Record<CsvField, number>> = {}
  CSV_HEADERS.forEach((field) => {
    const idx = header.indexOf(field)
    if (idx >= 0) colIndex[field] = idx
  })
  if (colIndex.name === undefined) {
    return {
      rows: [],
      headerError: `Header row must include a "name" column. Found: ${header.join(', ') || '(empty)'}`,
    }
  }

  const rows: ParsedRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]!).map((c) => c.trim())
    const get = (field: CsvField): string =>
      colIndex[field] !== undefined ? (cells[colIndex[field]!] ?? '') : ''

    const name = get('name')
    const description = get('description')
    const address = get('address')
    const latRaw = get('latitude')
    const lngRaw = get('longitude')
    const entity = get('entity')
    const stateVal = get('state')
    const areaRaw = get('branch_area_sqft')
    const category = get('category')
    const field1 = get('field1')
    const field2 = get('field2')
    const field3 = get('field3')
    const field4 = get('field4')
    const field5 = get('field5')
    const team = get('team')

    let error: string | null = null
    if (!name) error = 'Missing name'
    let latitude: number | null = null
    let longitude: number | null = null
    if (latRaw) {
      const n = Number(latRaw)
      if (Number.isFinite(n)) latitude = n
      else error = error ?? `Latitude "${latRaw}" is not a number`
    }
    if (lngRaw) {
      const n = Number(lngRaw)
      if (Number.isFinite(n)) longitude = n
      else error = error ?? `Longitude "${lngRaw}" is not a number`
    }
    let branchArea: number | null = null
    if (areaRaw) {
      // Strip optional thousand-separators (commas) the user might
      // type/copy from Excel before parsing.
      const cleaned = areaRaw.replace(/[, ]/g, '')
      const n = Number(cleaned)
      if (Number.isFinite(n) && Number.isInteger(n) && n >= 0) branchArea = n
      else error = error ?? `Branch area "${areaRaw}" must be a non-negative whole number`
    }

    rows.push({
      rowNum: i + 1, // 1-based + header offset → matches the user's spreadsheet
      name, description, address, latitude, longitude,
      entity, state: stateVal, branch_area_sqft: branchArea,
      category, field1, field2, field3, field4, field5,
      team,
      error,
    })
  }
  return { rows, headerError: null }
}

function downloadCsvTemplate() {
  // Two sample rows so the user sees what optional columns look like
  // both filled and empty. Quoted values use a comma in the address
  // to show the escaping rule.
  const csv =
    'name,description,address,latitude,longitude,entity,state,branch_area_sqft,category,field1,field2,field3,field4,field5,team\n' +
    'Main Plant,Primary manufacturing site,"100 Industrial Rd, Sector 4",12.9716,77.5946,HSE Operations,Tamil Nadu,18500,Branch,Region East,RM Alpha,Contract-2026-001,A-Tier,Active,Safety\n' +
    'Warehouse B,,Old Highway,,,,,,,,,,,,Facilities\n'
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'sites-import-template.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function ImportSitesDialog({
  open, onClose, onImported,
}: { open: boolean; onClose: () => void; onImported: (count: number) => void }) {
  const [filename, setFilename] = useState<string | null>(null)
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [headerError, setHeaderError] = useState<string | null>(null)
  const [submitErrors, setSubmitErrors] = useState<Map<number, string>>(new Map())
  // Default-open on first mount — admins building the import file for
  // the first time want the column guide visible up front. Once they
  // know the shape they can collapse it.
  const [helpOpen, setHelpOpen] = useState(true)

  function reset() {
    setFilename(null); setRows([]); setHeaderError(null); setSubmitErrors(new Map())
  }

  // Reset on close so re-opening the dialog doesn't show stale preview rows.
  useEffect(() => { if (!open) reset() }, [open])

  async function handleFile(file: File) {
    setSubmitErrors(new Map())
    const text = await file.text()
    const parsed = parseSitesCsv(text)
    setFilename(file.name)
    setRows(parsed.rows)
    setHeaderError(parsed.headerError)
  }

  const importM = useMutation({
    mutationFn: (payload: BulkCreateSiteRequest[]) => api.sites.bulkCreate(payload),
    onSuccess: (res) => onImported(res.created),
    onError: (e: any) => {
      // Server-side per-row validation (422 with `errors: [{row, message}]`)
      // — surface those alongside the preview so the user can pinpoint the
      // bad row in their spreadsheet.
      const errs = e?.payload?.errors as Array<{ row: number; message: string }> | undefined
      if (Array.isArray(errs)) {
        const m = new Map<number, string>()
        // `row` in the server response is 1-based against the payload;
        // we add 1 to align with the user's spreadsheet (which has a
        // header row above their first data row).
        errs.forEach((er) => m.set(er.row + 1, er.message))
        setSubmitErrors(m)
        toast.error('Some rows were rejected — see highlights')
        return
      }
      toast.error(e instanceof ApiError ? e.message : 'Import failed')
    },
  })

  const hasClientErrors = rows.length > 0 && rows.some((r) => r.error !== null)
  const canImport = rows.length > 0 && !hasClientErrors && !headerError && !importM.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>Import sites from CSV</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
          Each row creates one site. The import is all-or-nothing: if any row is
          invalid, no sites are created and you can fix the file and re-run.
          Up to 500 rows per file.
        </Typography>

        {/* Help / guide panel — same expandable pattern the Users
            import uses. Starts open so first-time admins see the
            full column list, an explainer for the new category +
            field1..5 slots, and a copy-pasteable example row. */}
        <Box sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
          mb: 2, overflow: 'hidden',
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
        }}>
          <Box onClick={() => setHelpOpen((v) => !v)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 1, cursor: 'pointer',
              '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)' },
            }}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
              How to build the CSV
            </Typography>
            {helpOpen
              ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          {helpOpen && (
            <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
              <Stack spacing={2}>
                {/* ── Columns reference ─────────────────────────── */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
                    Required column
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    <Box component="code" sx={{
                      bgcolor: (t) => `${t.palette.primary.main}14`, color: 'primary.main',
                      px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, mr: 0.5,
                    }}>name</Box>
                    <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                      — the site's display name. Everything else is optional.
                    </Typography>
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
                    Optional columns
                  </Typography>
                  <Stack spacing={0.75}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {[
                        ['description', 'Free-text — what the site is used for'],
                        ['address',     'Postal address (CSV-escape commas with quotes)'],
                        ['latitude',    'Decimal degrees, e.g. 12.9716'],
                        ['longitude',   'Decimal degrees, e.g. 77.5946'],
                        ['entity',      'Operating company / branch operator'],
                        ['state',       'State / region the site sits in'],
                        ['branch_area_sqft', 'Built-up area, non-negative integer'],
                      ].map(([col, hint]) => (
                        <Stack key={col} direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, mb: 0.5, mr: 1.5 }}>
                          <Box component="code" sx={{
                            bgcolor: 'action.hover', color: 'text.secondary',
                            px: 0.75, py: 0.1, borderRadius: 0.5, fontWeight: 700, fontSize: 11.5,
                            whiteSpace: 'nowrap',
                          }}>{col}</Box>
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{hint}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>

                {/* ── New: Category + 5 custom fields explainer ── */}
                <Box sx={{
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: (t) => `${t.palette.primary.main}10`,
                  border: '1px solid', borderColor: (t) => `${t.palette.primary.main}30`,
                }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'primary.main', mb: 1 }}>
                    Category & custom fields
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    {['category', 'field1', 'field2', 'field3', 'field4', 'field5'].map((col) => (
                      <Box key={col} component="code" sx={{
                        bgcolor: 'background.paper', color: 'primary.main',
                        px: 0.75, py: 0.2, borderRadius: 0.5, fontWeight: 700, fontSize: 11.5,
                        border: '1px solid', borderColor: (t) => `${t.palette.primary.main}30`,
                      }}>
                        {col}
                      </Box>
                    ))}
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.55 }}>
                    <strong>category</strong> is a free-text bucket (e.g. <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Branch</Box>, <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Warehouse</Box>, <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Datacentre</Box>).{' '}
                    <strong>field1–field5</strong> are five extra free-text slots you can use today for anything you want to track per site — asset tag, regional manager, contract id, tier, status. The column names stay generic on the database; the labels in the form will be renamed to something meaningful in a future release without you needing to re-import.
                  </Typography>
                </Box>

                {/* ── HSE fork: team explainer ────────────────────── */}
                <Box sx={{
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: '#3B82F610',
                  border: '1px solid', borderColor: '#3B82F630',
                }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: '#1D4ED8', mb: 1 }}>
                    Responsible team
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Box component="code" sx={{
                      bgcolor: 'background.paper', color: '#1D4ED8',
                      px: 0.75, py: 0.2, borderRadius: 0.5, fontWeight: 700, fontSize: 11.5,
                      border: '1px solid', borderColor: '#3B82F630',
                    }}>
                      team
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.55 }}>
                    Name of the team that should own risk reports scanned from this site's QR sticker (e.g.{' '}
                    <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Safety</Box>,{' '}
                    <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: 11 }}>Facilities</Box>). Case-insensitive but must match an existing team's name exactly — the whole import is rolled back if a name doesn't resolve, so you can fix a typo without duplicates. Leave blank to import the site with no team linked; you can assign one later from the site's detail panel.
                  </Typography>
                </Box>

                {/* HSE fork: "Auto-create default building + attach
                    checklist" explainer block removed with the
                    columns it documented. */}

                {/* ── Tips ──────────────────────────────────────── */}
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.55 }}>
                  Column order doesn't matter — the header row drives the
                  mapping. Leave a cell blank to skip a value on that row.
                  CSV-escape any address that contains a comma by wrapping the
                  whole address in <Box component="code" sx={{ bgcolor: 'action.hover',
                    px: 0.5, borderRadius: 0.5, fontSize: 11 }}>"double quotes"</Box>.
                  Hit <strong>Download template</strong> below for a starter
                  file with the exact headers and an example row.
                </Typography>

                {/* ── Example row ───────────────────────────────── */}
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
                    Example row
                  </Typography>
                  <Box sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    p: 1.25, bgcolor: 'background.paper',
                    fontFamily: 'ui-monospace, monospace', fontSize: 11.5,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    color: 'text.secondary',
                  }}>
                    name,description,address,latitude,longitude,entity,state,branch_area_sqft,category,field1,field2,field3,field4,field5,team{'\n'}
                    Main Plant,Primary manufacturing site,"100 Industrial Rd, Sector 4",12.9716,77.5946,HSE Operations,Tamil Nadu,18500,Branch,Region East,RM Alpha,Contract-2026-001,A-Tier,Active,Safety
                  </Box>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={1.25} sx={{ mb: 2 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: 13, fontWeight: 600 }}
          >
            {filename ? 'Choose another file' : 'Choose CSV file'}
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) handleFile(f)
                // Allow re-selecting the same filename later.
                e.currentTarget.value = ''
              }}
            />
          </Button>
          <Button
            variant="text"
            startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={downloadCsvTemplate}
            sx={{ fontSize: 13, fontWeight: 600 }}
          >
            Download template
          </Button>
        </Stack>

        {filename && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.25 }}>
            File: <strong>{filename}</strong> · {rows.length} data row{rows.length === 1 ? '' : 's'}
          </Typography>
        )}

        {headerError && (
          <Box sx={{ p: 1.5, mb: 1.5, borderRadius: 1.5, bgcolor: 'error.main', color: '#fff' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{headerError}</Typography>
          </Box>
        )}

        {rows.length > 0 && (
          <Box sx={{ maxHeight: 340, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <Box component="thead" sx={{
                position: 'sticky', top: 0, bgcolor: (t) => t.palette.mode === 'light' ? '#F8FAFC' : 'rgba(255,255,255,0.04)',
                '& th': { textAlign: 'left', fontWeight: 700, color: 'text.secondary', px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' },
              }}>
                <tr>
                  <th style={{ width: 56 }}>Row</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Address</th>
                  <th style={{ width: 90 }}>Lat</th>
                  <th style={{ width: 90 }}>Lng</th>
                  <th>Issue</th>
                </tr>
              </Box>
              <Box component="tbody" sx={{
                '& td': { px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top' },
              }}>
                {rows.map((r) => {
                  const serverErr = submitErrors.get(r.rowNum) ?? null
                  const err = r.error ?? serverErr
                  return (
                    <tr key={r.rowNum} style={err ? { background: 'rgba(220,38,38,0.06)' } : undefined}>
                      <td style={{ color: 'rgba(0,0,0,0.5)', fontFamily: 'ui-monospace, monospace' }}>{r.rowNum}</td>
                      <td style={{ fontWeight: 600 }}>{r.name || <em>—</em>}</td>
                      <td>{r.description || <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td>{r.address || <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td style={{ fontFamily: 'ui-monospace, monospace' }}>{r.latitude ?? <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td style={{ fontFamily: 'ui-monospace, monospace' }}>{r.longitude ?? <em style={{ color: 'rgba(0,0,0,0.4)' }}>—</em>}</td>
                      <td style={{ color: '#DC2626', fontWeight: err ? 600 : 400 }}>{err ?? ''}</td>
                    </tr>
                  )
                })}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canImport}
          onClick={() => importM.mutate(rows.map((r) => ({
            name: r.name,
            description: r.description || undefined,
            address: r.address || undefined,
            latitude: r.latitude ?? undefined,
            longitude: r.longitude ?? undefined,
            entity: r.entity || undefined,
            state: r.state || undefined,
            branch_area_sqft: r.branch_area_sqft ?? undefined,
            category: r.category || undefined,
            field1: r.field1 || undefined,
            field2: r.field2 || undefined,
            field3: r.field3 || undefined,
            field4: r.field4 || undefined,
            field5: r.field5 || undefined,
            // HSE fork: responsible-team name-based lookup. Backend
            // resolves the name to a team id or rejects the row with
            // a helpful "team not found" error.
            ...(r.team ? { team: r.team } : {}),
          })))}
          sx={{ fontSize: 13, fontWeight: 600 }}
        >
          {importM.isPending
            ? 'Importing…'
            : `Import ${rows.length} site${rows.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// HSE fork: renders a scannable QR that encodes the public risk-report
// URL for a single site (`<host>/report/<orgSlug>/<siteId>`). The QR
// dialog exposes an "expand" affordance so admins can print it at a
// larger size for wall stickers. Falls back to a login-prompt hint
// when the auth store hasn't hydrated (rare — this component is only
// rendered inside the authenticated Sites page).
function SiteQrCard({ site }: { site: Site }) {
  const user = useAuthStore((s) => s.user)
  const orgSlug = user?.org_slug ?? ''
  const [dialogOpen, setDialogOpen] = useState(false)

  if (!orgSlug) {
    return (
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
        Sign in with an org context to generate the site's public QR code.
      </Typography>
    )
  }

  // The reporting page reads the URL from `window.location.origin`, so
  // we use the same origin here — QRs printed from a staging deploy
  // scan into staging, production into production. Falls back to a
  // relative path when SSR-ish contexts leave `origin` undefined.
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const publicUrl = `${origin}/report/${encodeURIComponent(orgSlug)}/${encodeURIComponent(site.id)}`

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ p: 1.25, bgcolor: '#fff', borderRadius: 1.5, boxShadow: (t) =>
              t.palette.mode === 'light' ? '0 1px 3px rgba(0,0,0,0.08)' : '0 0 0 1px rgba(255,255,255,0.06)' }}>
          <QRCodeCanvas value={publicUrl} size={96} level="M" />
        </Box>
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            Scan to report a risk at this site
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', wordBreak: 'break-all' }}>
            {publicUrl}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => setDialogOpen(true)}
              sx={{ fontSize: 12, textTransform: 'none' }}>
              Show larger / download
            </Button>
            <Button size="small" variant="text"
              onClick={() => navigator.clipboard.writeText(publicUrl).then(
                () => toast.success('Public URL copied'),
                () => toast.error("Couldn't copy — copy the URL manually"),
              )}
              sx={{ fontSize: 12, textTransform: 'none' }}>
              Copy URL
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <QrCodeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        payload={publicUrl}
        title={site.name}
        subtitle="Public risk-report intake"
        kind="Site"
      />
    </>
  )
}

// HSE fork: shows the site's currently-linked "responsible team",
// the team's member roster, and a Reassign affordance that swaps the
// linkage in-place. Kept next to the QR card because both surfaces
// answer the same operational question — "who owns this site when a
// public risk report lands?".
function SiteTeamCard({ site }: { site: Site }) {
  const qc = useQueryClient()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [draftTeamId, setDraftTeamId] = useState<string | null>(
    (site as (Site & { team_id?: string | null })).team_id ?? null,
  )

  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.teams.list(),
  })
  const teams: Team[] = Array.isArray(teamsQuery.data) ? teamsQuery.data : []
  const currentTeamId = (site as (Site & { team_id?: string | null })).team_id ?? null
  const currentTeam = teams.find((t) => t.id === currentTeamId) ?? null

  const membersQuery = useQuery({
    queryKey: ['teams', currentTeamId, 'members'],
    queryFn: () => currentTeamId ? api.teams.listMembers(currentTeamId) : Promise.resolve([]),
    enabled: !!currentTeamId,
  })
  const members = Array.isArray(membersQuery.data) ? membersQuery.data : []

  const reassignM = useMutation({
    mutationFn: (nextTeamId: string | null) =>
      api.sites.update(site.id, { team_id: nextTeamId ?? null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] })
      qc.invalidateQueries({ queryKey: ['teams'] })
      toast.success('Team reassigned')
      setPickerOpen(false)
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : "Couldn't reassign the team"),
  })

  return (
    <>
      <Stack spacing={1.75}>
        {currentTeam ? (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: primary.hover,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid', borderColor: 'primary.main' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'primary.main' }}>
                {currentTeam.name.charAt(0).toUpperCase()}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                {currentTeam.name}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                {membersQuery.isLoading ? 'Loading members…' : `${members.length} member${members.length === 1 ? '' : 's'}`}
              </Typography>
            </Box>
            <Button size="small" variant="outlined"
              onClick={() => { setDraftTeamId(currentTeamId); setPickerOpen(true) }}
              sx={{ fontSize: 12, textTransform: 'none' }}>
              Reassign
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2,
                border: '1px dashed', borderColor: 'divider',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HelpOutlineOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.secondary' }}>
                No responsible team
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                Public risk reports for this site land in the queue unassigned.
              </Typography>
            </Box>
            <Button size="small" variant="outlined"
              onClick={() => { setDraftTeamId(null); setPickerOpen(true) }}
              sx={{ fontSize: 12, textTransform: 'none' }}>
              Assign
            </Button>
          </Stack>
        )}

        {/* Roster */}
        {currentTeam && !membersQuery.isLoading && members.length > 0 && (
          <Box sx={{ pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
              Members
            </Typography>
            <Stack spacing={0.75}>
              {members.map((m) => (
                <Stack key={m.id} direction="row" alignItems="center" spacing={1}>
                  <UserAvatar name={m.name} avatarUrl={m.avatar_url}
                    size={22} sx={{ bgcolor: '#0D9488' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.name}
                    </Typography>
                    {m.email && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.email}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
        {currentTeam && !membersQuery.isLoading && members.length === 0 && (
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic' }}>
            This team has no members yet — add some from Settings → Roles.
          </Typography>
        )}
      </Stack>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Reassign responsible team
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
            Pick the team that should own risk reports for {site.name}.
            Choose "No team" to leave reports in the untriaged queue.
          </Typography>
          <TextField
            select fullWidth size="small" label="Team"
            value={draftTeamId ?? ''}
            onChange={(e) => setDraftTeamId(e.target.value === '' ? null : e.target.value)}
            slotProps={{
              select: { native: true },
              inputLabel: { shrink: true },
            }}
          >
            <option value="">— No team —</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPickerOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained"
            disabled={draftTeamId === currentTeamId || reassignM.isPending}
            onClick={() => reassignM.mutate(draftTeamId)}
            sx={{ fontSize: 13, fontWeight: 700 }}>
            {reassignM.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
